/**
 * Flight-mode interface kit: inked sticker buttons, dark glass panels, bars
 * and medallions. Hits use the shared Painter so accessibility labels, press
 * feedback and tablet touch targets behave like the rest of the app.
 */
import type { Painter } from "../../../client/paint";
import { FONT, INK, NUM_FONT, TAU, box, clamp, disc, linear, poly, radial, rgba, roundRect, shade } from "./kit";
import { icon } from "./icons";

type Ctx = CanvasRenderingContext2D;
export type Tone = "gold" | "teal" | "cream" | "dark" | "danger" | "sky";
export const TONES: Record<Tone, { top: string; bottom: string; text: string; edge: string }> = {
  gold: { top: "#FFE08A", bottom: "#F2A93B", text: INK, edge: INK },
  teal: { top: "#7FF0D8", bottom: "#23A99A", text: INK, edge: INK },
  cream: { top: "#FFFDF6", bottom: "#F2E9D6", text: "#2E3542", edge: INK },
  dark: { top: "#3A4460", bottom: "#1F2638", text: "#F4F1E6", edge: "#0E121C" },
  danger: { top: "#FF8A8A", bottom: "#E0475B", text: "#FFFFFF", edge: INK },
  sky: { top: "#9FDBFF", bottom: "#3E9BDC", text: INK, edge: INK },
};
export const GROUP_COLORS: Record<string, string> = {
  通用: "#6BC7A8",
  主炮: "#F2A93B",
  副武器: "#6FA8FF",
  弹道: "#C98BFF",
  进化: "#FFC94D",
};

function hit(p: Painter, id: string, label: string, x: number, y: number, w: number, h: number, action: () => void, disabled: boolean) {
  const grow = p.tablet ? Math.max(0, 44 - h) / 2 : 0;
  p.hits.push({ id, label, x, y: y - grow, w, h: h + grow * 2, disabled, action });
}
function text(c: Ctx, value: string, x: number, y: number, size: number, color: string, weight = "800", align: CanvasTextAlign = "center", font = FONT) {
  c.font = `${weight} ${size}px ${font}`;
  c.textAlign = align;
  c.textBaseline = "middle";
  c.fillStyle = color;
  c.fillText(value, x, y);
}
export function fitText(c: Ctx, value: string, x: number, y: number, width: number, size: number, color: string, weight = "700", align: CanvasTextAlign = "left") {
  c.font = `${weight} ${size}px ${FONT}`;
  let out = value;
  if (c.measureText(out).width > width) {
    out = "";
    for (const ch of value) {
      if (c.measureText(out + ch + "…").width > width) break;
      out += ch;
    }
    out += "…";
  }
  text(c, out, x, y, size, color, weight, align);
}

/** Chunky sticker button: ink outline, glossy gradient and a drop "lip". */
export function stickerButton(
  p: Painter,
  id: string,
  label: string,
  x: number,
  y: number,
  w: number,
  h: number,
  action: () => void,
  tone: Tone = "cream",
  disabled = false,
  glyph?: string,
  caption?: string,
) {
  const c = p.ctx,
    t = disabled ? TONES.cream : TONES[tone],
    held = p.pressedId === id && !disabled,
    lip = held ? 1 : 4,
    r = Math.min(h / 2, 16);
  c.save();
  if (disabled) c.globalAlpha = 0.55;
  box(c, x, y + lip, w, h - lip, r, shade(t.bottom, -0.35), t.edge, 2.4);
  const yy = y + (held ? 3 : 0);
  box(c, x, yy, w, h - 4, r, linear(c, 0, yy, 0, yy + h, [[0, t.top], [1, t.bottom]]), t.edge, 2.4);
  c.save();
  roundRect(c, x + 4, yy + 3, w - 8, (h - 4) * 0.42, r * 0.7);
  c.fillStyle = "rgba(255,255,255,0.28)";
  c.fill();
  c.restore();
  const size = h < 40 ? 14 : 16,
    color = disabled ? "#8C8674" : t.text;
  c.font = `800 ${size}px ${FONT}`;
  const gw = glyph ? size + 6 : 0,
    tw = Math.min(c.measureText(label).width, w - 20 - gw),
    cy = yy + (h - 4) / 2 - (caption ? 6 : 0);
  if (glyph) icon(c, glyph, x + w / 2 - (tw + gw) / 2 + size / 2, cy, size + 2, tone === "dark" ? "#FFE08A" : "#FFFFFF", disabled ? "#8C8674" : INK);
  fitText(c, label, x + w / 2 + gw / 2, cy + 0.5, w - 20 - gw, size, color, "800", "center");
  if (caption) fitText(c, caption, x + w / 2, cy + 16, w - 16, 10.5, rgba(color, 0.75), "600", "center");
  c.restore();
  hit(p, id, label, x, y, w, h, action, disabled);
}

/** Round icon button for the battle HUD. */
export function roundButton(
  p: Painter,
  id: string,
  label: string,
  glyph: string,
  cx: number,
  cy: number,
  r: number,
  action: () => void,
  tone: Tone = "dark",
  disabled = false,
  off = false,
) {
  const c = p.ctx,
    t = TONES[tone],
    held = p.pressedId === id && !disabled;
  c.save();
  if (disabled) c.globalAlpha = 0.6;
  disc(c, cx, cy + 3, r, shade(t.bottom, -0.4), t.edge, 2.2);
  const y = cy + (held ? 2 : 0);
  disc(c, cx, y, r, radial(c, cx - r * 0.3, y - r * 0.4, 0, r * 1.3, [[0, t.top], [1, t.bottom]]), t.edge, 2.2);
  icon(c, glyph, cx, y, r * 1.05, off ? "#8A93A6" : tone === "dark" ? "#F4F1E6" : "#FFFFFF", tone === "dark" ? "#0E121C" : INK);
  c.restore();
  hit(p, id, label, cx - r, cy - r, r * 2, r * 2, action, disabled);
}

/** Dark glass panel with an inked rim and a faint top sheen. */
export function glass(c: Ctx, x: number, y: number, w: number, h: number, r = 16, alpha = 0.78, shadow = true) {
  if (shadow) box(c, x, y + 3, w, h, r, "rgba(8,10,20,0.3)", null);
  box(c, x, y, w, h, r, rgba("#161C2E", alpha), "rgba(255,255,255,0.18)", 1.5);
  roundRect(c, x + 2, y + 2, w - 4, Math.min(18, h / 2), r - 2);
  c.fillStyle = "rgba(255,255,255,0.06)";
  c.fill();
}
/** Warm paper card used for dialogs, matching the app's paper-post style. */
export function paper(c: Ctx, x: number, y: number, w: number, h: number, r = 22) {
  box(c, x + 2, y + 6, w, h, r, "rgba(10,14,30,0.35)", null);
  box(c, x, y, w, h, r, linear(c, 0, y, 0, y + h, [[0, "#FFFCF3"], [1, "#F4EBD8"]]), INK, 3);
  c.save();
  c.setLineDash([6, 5]);
  roundRect(c, x + 8, y + 8, w - 16, h - 16, r - 6);
  c.strokeStyle = "rgba(160,130,90,0.35)";
  c.lineWidth = 1.5;
  c.stroke();
  c.restore();
}
export function ribbon(c: Ctx, label: string, cx: number, cy: number, w: number, color = "#E0475B") {
  const h = 38;
  poly(c, [[cx - w / 2 - 18, cy - h / 2 + 6], [cx - w / 2, cy - h / 2 + 6], [cx - w / 2, cy + h / 2 + 6], [cx - w / 2 - 18, cy + h / 2 + 6], [cx - w / 2 - 8, cy + 6]], shade(color, -0.3), INK, 2.4);
  poly(c, [[cx + w / 2 + 18, cy - h / 2 + 6], [cx + w / 2, cy - h / 2 + 6], [cx + w / 2, cy + h / 2 + 6], [cx + w / 2 + 18, cy + h / 2 + 6], [cx + w / 2 + 8, cy + 6]], shade(color, -0.3), INK, 2.4);
  box(c, cx - w / 2, cy - h / 2, w, h, 6, linear(c, 0, cy - h / 2, 0, cy + h / 2, [[0, shade(color, 0.2)], [1, color]]), INK, 2.6);
  c.lineJoin = "round";
  c.font = `900 19px ${FONT}`;
  c.textAlign = "center";
  c.textBaseline = "middle";
  c.strokeStyle = INK;
  c.lineWidth = 4;
  c.strokeText(label, cx, cy + 1);
  c.fillStyle = "#FFF6E4";
  c.fillText(label, cx, cy + 1);
}
export function medallion(c: Ctx, glyph: string, cx: number, cy: number, r: number, color: string, star = false) {
  disc(c, cx, cy + 2.5, r, shade(color, -0.45), INK, 2.4);
  disc(c, cx, cy, r, radial(c, cx - r * 0.35, cy - r * 0.4, 0, r * 1.3, [[0, shade(color, 0.45)], [1, color]]), INK, 2.4);
  icon(c, glyph, cx, cy, r * 1.15, "#FFFFFF");
  if (star) {
    c.save();
    c.translate(cx + r * 0.72, cy - r * 0.72);
    icon(c, "star", 0, 0, r * 0.8, "#FFE08A");
    c.restore();
  }
}
/** Segmented bar with ink outline; `extra` (e.g. shield) overlays in cyan. */
export function meter(c: Ctx, x: number, y: number, w: number, h: number, value: number, color: string, extra = 0, segments = 0) {
  const k = clamp(value, 0, 1);
  box(c, x, y, w, h, h / 2, "rgba(10,12,24,0.75)", INK, 2);
  if (k > 0) {
    c.save();
    roundRect(c, x + 2, y + 2, w - 4, h - 4, (h - 4) / 2);
    c.clip();
    c.fillStyle = linear(c, 0, y, 0, y + h, [[0, shade(color, 0.35)], [0.5, color], [1, shade(color, -0.2)]]);
    c.fillRect(x + 2, y + 2, (w - 4) * k, h - 4);
    if (extra > 0) {
      c.fillStyle = "rgba(143,232,255,0.85)";
      c.fillRect(x + 2, y + 2, (w - 4) * clamp(extra, 0, 1), (h - 4) * 0.45);
    }
    c.fillStyle = "rgba(255,255,255,0.3)";
    c.fillRect(x + 2, y + 2, (w - 4) * k, (h - 4) * 0.35);
    if (segments > 1) {
      c.fillStyle = "rgba(10,12,24,0.35)";
      for (let i = 1; i < segments; i++) c.fillRect(x + (w * i) / segments - 1, y, 2, h);
    }
    c.restore();
  }
}
export function chip(c: Ctx, x: number, y: number, label: string, glyph: string, color: string, maxWidth: number, textColor = "#F4F1E6", light = false) {
  c.font = `700 12px ${FONT}`;
  const w = Math.min(maxWidth, c.measureText(label).width + 40);
  const cy = y + (light ? 10 : 12);
  glass(c, x, y, w, light ? 20 : 24, 10, light ? 0.24 : 0.72, !light);
  if (!light) disc(c, x + 12, cy, 9, color, INK, 1.5);
  icon(c, glyph, x + 12, cy, light ? 14 : 12, light ? color : "#FFFFFF");
  c.save();
  if (light) {
    c.shadowColor = "#101624";
    c.shadowBlur = 3;
    c.shadowOffsetY = 1;
  }
  fitText(c, label, x + 26, cy + 0.5, w - 32, 12, textColor, "700");
  c.restore();
  return w;
}
export function numberText(c: Ctx, value: string, x: number, y: number, size: number, color: string, align: CanvasTextAlign = "left") {
  c.font = `900 ${size}px ${NUM_FONT}`;
  c.textAlign = align;
  c.textBaseline = "middle";
  c.lineJoin = "round";
  c.strokeStyle = INK;
  c.lineWidth = Math.max(3, size * 0.2);
  c.strokeText(value, x, y);
  c.fillStyle = color;
  c.fillText(value, x, y);
}
export function outlined(c: Ctx, value: string, x: number, y: number, size: number, color: string, align: CanvasTextAlign = "center", stroke = INK) {
  c.font = `900 ${size}px ${FONT}`;
  c.textAlign = align;
  c.textBaseline = "middle";
  c.lineJoin = "round";
  c.strokeStyle = stroke;
  c.lineWidth = Math.max(3, size * 0.22);
  c.strokeText(value, x, y);
  c.fillStyle = color;
  c.fillText(value, x, y);
}
/** Cooldown sweep over a round button. */
export function sweep(c: Ctx, cx: number, cy: number, r: number, k: number) {
  if (k <= 0) return;
  c.save();
  c.beginPath();
  c.moveTo(cx, cy);
  c.arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + TAU * clamp(k, 0, 1));
  c.closePath();
  c.fillStyle = "rgba(8,10,20,0.55)";
  c.fill();
  c.restore();
}
/** Blueprint hangar backdrop for the ship preview card. */
export function blueprint(c: Ctx, x: number, y: number, w: number, h: number, r = 24) {
  c.save();
  box(c, x + 2, y + 5, w, h, r, "rgba(20,30,50,0.25)", null);
  roundRect(c, x, y, w, h, r);
  c.fillStyle = linear(c, 0, y, 0, y + h, [[0, "#23355A"], [1, "#152036"]]);
  c.fill();
  c.clip();
  c.strokeStyle = "rgba(140,190,255,0.12)";
  c.lineWidth = 1;
  c.beginPath();
  for (let gx = x + 12; gx < x + w; gx += 18) {
    c.moveTo(gx, y);
    c.lineTo(gx, y + h);
  }
  for (let gy = y + 12; gy < y + h; gy += 18) {
    c.moveTo(x, gy);
    c.lineTo(x + w, gy);
  }
  c.stroke();
  c.strokeStyle = "rgba(140,190,255,0.22)";
  c.beginPath();
  for (let gx = x + 12; gx < x + w; gx += 90) {
    c.moveTo(gx, y);
    c.lineTo(gx, y + h);
  }
  c.stroke();
  c.fillStyle = radial(c, x + w / 2, y + h * 0.62, 0, Math.max(w, h) * 0.55, [[0, "rgba(120,230,255,0.28)"], [1, "rgba(120,230,255,0)"]]);
  c.fillRect(x, y, w, h);
  c.restore();
  roundRect(c, x, y, w, h, r);
  c.strokeStyle = INK;
  c.lineWidth = 3;
  c.stroke();
}
/** Launch pad ring under a displayed ship. */
export function pad(c: Ctx, cx: number, cy: number, rx: number, tick: number) {
  c.save();
  c.strokeStyle = "rgba(120,230,255,0.55)";
  c.lineWidth = 2;
  c.beginPath();
  c.ellipse(cx, cy, rx, rx * 0.3, 0, 0, TAU);
  c.stroke();
  c.setLineDash([8, 8]);
  c.lineDashOffset = -tick * 0.02;
  c.beginPath();
  c.ellipse(cx, cy, rx * 1.2, rx * 0.36, 0, 0, TAU);
  c.stroke();
  c.restore();
}
export { text as label };
/** Darken the battlefield behind a dialog (covers the status-bar bleed too). */
export function dim(c: Ctx, w: number, h: number, top = 0) {
  c.fillStyle = "rgba(10,14,30,0.58)";
  c.fillRect(0, -top, w, h + top);
}
/** Row of small stat tiles: [glyph, value, caption]. */
export function statTiles(c: Ctx, x: number, y: number, w: number, items: [string, string, string][]) {
  const gap = 8,
    tw = (w - gap * (items.length - 1)) / items.length;
  items.forEach(([glyph, value, caption], i) => {
    const tx = x + i * (tw + gap);
    box(c, tx, y, tw, 70, 12, "rgba(35,53,90,0.07)", "rgba(35,53,90,0.25)", 1.5);
    icon(c, glyph, tx + tw / 2, y + 17, 16, ["#6BC7A8", "#FFC94D", "#FF8A8A", "#6FA8FF"][i % 4]);
    c.font = `900 17px ${NUM_FONT}`;
    fitText(c, value, tx + tw / 2, y + 40, tw - 8, 17, INK, "900", "center");
    fitText(c, caption, tx + tw / 2, y + 58, tw - 8, 10.5, "#7C8594", "700", "center");
  });
}
export const EVENT_GLYPH: Record<string, string> = { formation: "lanes", elite: "precision", convoy: "salvage", recovery: "repair" };
export const EVENT_COLOR: Record<string, string> = { formation: "#6FA8FF", elite: "#FFC94D", convoy: "#6BC7A8", recovery: "#C98BFF" };
